import { Link } from "react-router-dom";
import { ArrowRight, Camera, MapPin, Infinity as InfinityIcon, SlidersHorizontal, Wand2 } from "lucide-react";
import Reveal, { SectionHeading } from "@/components/Reveal";
import Marquee from "@/components/Marquee";
import { useSiteContent } from "@/hooks/useSiteContent";

export default function About() {
  const content = useSiteContent();

  return (
    <main data-testid="about-page" className="pt-20">
      <section className="max-w-7xl mx-auto px-6 lg:px-10 py-20 lg:py-28">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center">
          <Reveal>
            <div className="relative">
              <div className="absolute -inset-4 border border-[#D4AF37]/25" />
              <img
                src={content.about_image || "https://images.unsplash.com/photo-1587397845856-e6cf49176c70?auto=format&fit=crop&w=1200&q=85"}
                alt="Daniel Dulude, photographe"
                data-testid="about-page-photo"
                className="w-full aspect-[4/5] object-cover"
              />
            </div>
          </Reveal>
          <div>
            <SectionHeading center={false} number="01" eyebrow="Mon parcours" title="À propos de Daniel Dulude"
                            subtitle="13 ans derrière l'objectif, mais une créativité qui vient de beaucoup plus loin." />
            <Reveal delay={0.2}>
              <div className="text-slate-300 leading-loose mt-8 space-y-5 text-sm sm:text-base" data-testid="about-bio">
                <p>
                  Depuis toujours, j'aime créer. Bien avant la photographie, je dessinais énormément et j'ai même
                  participé à des concours de dessin dans ma jeunesse. Cette passion pour l'image et cette imagination
                  m'ont naturellement accompagné vers la photographie.
                </p>
                <p>
                  Aujourd'hui, après plus de 13 ans de photographie, je travaille principalement en solo. J'aime cette
                  façon de travailler : être présent, observer, chercher la bonne lumière et créer une image qui
                  correspond vraiment à la personne ou au moment que je photographie.
                </p>
                <p>
                  Pour les projets plus importants, je peux toutefois être accompagné d'une personne de confiance,
                  notamment ma sœur, afin de répondre aux besoins du contrat et de bien gérer une production plus
                  importante.
                </p>
              </div>
            </Reveal>
            <Reveal delay={0.3}>
              <div className="flex flex-col gap-5 mt-10" data-testid="about-highlights">
                {[
                  [Camera, "13 ans d'expérience en photographie"],
                  [MapPin, "Saint-Jean-sur-Richelieu et les environs"],
                  [InfinityIcon, "Des souvenirs pour la vie"],
                ].map(([Icon, text]) => (
                  <div key={text} className="flex items-center gap-4 border-l border-[#D4AF37]/40 pl-5">
                    <Icon size={18} className="text-[#D4AF37] shrink-0" />
                    <p className="text-slate-300 text-sm">{text}</p>
                  </div>
                ))}
              </div>
            </Reveal>
            <Reveal delay={0.4}>
              <Link to="/contact" data-testid="about-contact-button" className="btn-gold mt-10">
                Me contacter <ArrowRight size={15} />
              </Link>
            </Reveal>
          </div>
        </div>
      </section>

      <Marquee />

      <section className="max-w-4xl mx-auto px-6 py-24" data-testid="approach-section">
        <SectionHeading number="02" eyebrow="Mon approche" title="Ma façon de photographier" />
        <Reveal delay={0.2}>
          <p className="text-slate-300 leading-loose mt-10 text-center text-sm sm:text-base" data-testid="approach-text">
            Je recherche avant tout des images naturelles et authentiques. Mais j'aime aussi laisser place à ma
            créativité. Certaines photographies peuvent recevoir une retouche plus poussée ou un traitement artistique
            afin de leur donner ce petit effet « wow », tout en conservant une image qui reste fidèle au sujet.
          </p>
        </Reveal>
      </section>

      <section className="bg-[#0D0D10] border-y border-[#D4AF37]/10 py-24" data-testid="tools-section">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <SectionHeading number="03" eyebrow="Mon atelier" title="Mes outils de création"
                          subtitle="Pour donner vie à mes photographies, mes deux principaux outils de travail sont :" />
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-16 max-w-4xl mx-auto">
            <Reveal>
              <div className="gold-card p-10 text-center h-full" data-testid="tool-lightroom">
                <div className="w-14 h-14 mx-auto border border-[#D4AF37]/40 rotate-45 flex items-center justify-center mb-8">
                  <SlidersHorizontal size={22} className="text-[#D4AF37] -rotate-45" />
                </div>
                <h3 className="font-serif text-2xl text-[#F8F9FA]">Adobe Lightroom</h3>
                <p className="text-slate-400 text-sm leading-relaxed mt-4">
                  Pour le développement, les ajustements et la préparation de mes photographies.
                </p>
              </div>
            </Reveal>
            <Reveal delay={0.12}>
              <div className="gold-card p-10 text-center h-full" data-testid="tool-photoshop">
                <div className="w-14 h-14 mx-auto border border-[#D4AF37]/40 rotate-45 flex items-center justify-center mb-8">
                  <Wand2 size={22} className="text-[#D4AF37] -rotate-45" />
                </div>
                <h3 className="font-serif text-2xl text-[#F8F9FA]">Adobe Photoshop</h3>
                <p className="text-slate-400 text-sm leading-relaxed mt-4">
                  Pour les retouches plus précises, les compositions et les créations artistiques.
                </p>
              </div>
            </Reveal>
          </div>
          <Reveal delay={0.2}>
            <p className="text-slate-400 text-sm leading-relaxed text-center mt-10 max-w-2xl mx-auto" data-testid="equipment-note">
              Je dispose également de mon propre équipement photographique et d'éclairage, ce qui me permet de
              travailler aussi bien en studio qu'à l'extérieur.
            </p>
          </Reveal>
        </div>
      </section>

      <section className="max-w-4xl mx-auto px-6 py-24 text-center" data-testid="about-signature-section">
        <Reveal>
          <p className="font-serif italic text-2xl sm:text-3xl text-slate-200 leading-relaxed" data-testid="conclusion-quote">
            « Pour moi, une photographie ne consiste pas seulement à appuyer sur le déclencheur. C'est une façon de
            transformer une idée, une lumière ou une émotion en image. »
          </p>
          <div className="gold-line w-40 mx-auto mt-10" />
          <p className="font-script text-3xl gold-text mt-8">Daniel Dulude</p>
        </Reveal>
      </section>
    </main>
  );
}
